// components/models/GrokImagine2ControlPanel.tsx
import React, { useCallback, memo } from 'react';
import AutoGrowingTextarea from '../AutoGrowingTextarea';
import Select from '../ui/Select';
import { GrokImagine2Params, GrokImagine2Resolution } from '#types/types';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';

export const getDefaultGrokImagine2Params = (): GrokImagine2Params => ({
  prompt: '',
  seed: -1,
  resolution: '2K',
  aspect_ratio: 'auto',
});

const RESOLUTION_OPTIONS = ['2K', '1K'];
const SELECT_STYLE = { width: '100%' };

const GrokImagine2ControlPanel: React.FC = () => {
  const { kernel } = useKernel();
  const params = useKernelStore(
    state => state.modelParams[state.diffusionModel] as GrokImagine2Params | undefined
  );

  const updateParam = useCallback((name: string, value: string) => {
    const diffusionModel = kernel.current?.getDiffusionModel();
    if (!diffusionModel) return;

    kernel.current?.setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) return prevModelParams;
      return { ...prevModelParams, [diffusionModel]: { ...currentParams, [name]: value } };
    });

    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      value_length: value.length,
      model_name: diffusionModel,
      source_component: 'GrokImagine2ControlPanel',
    });
  }, [kernel]);

  const handleParamChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    updateParam(e.target.name, e.target.value);
  }, [updateParam]);

  const handleResolutionChange = useCallback((val: string) => {
    updateParam('resolution', val as GrokImagine2Resolution);
  }, [updateParam]);

  if (!params) return null;

  return (
    <>
      <div>
        <AutoGrowingTextarea
          label="Prompt"
          placeholder="Describe what you want to change..."
          name="prompt"
          value={params.prompt}
          onChange={handleParamChange}
          onEnterPress={kernel.current?.triggerGenerate}
          maxHeight={600}
          labelClassName="block text-lg font-semibold text-white mb-2"
        />
      </div>

      <div className="mt-4">
        <Select
          id="resolution"
          label="Resolution"
          value={params.resolution}
          options={RESOLUTION_OPTIONS}
          onChange={handleResolutionChange}
          style={SELECT_STYLE}
        />
      </div>
    </>
  );
};

export default memo(GrokImagine2ControlPanel);
